import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import FacilityIcon from "@/components/FacilityIcon";
import OperatorCover from "@/components/OperatorCover";
import { api, formatGBP, FACILITY_LABELS } from "@/lib/api";
import { Star, ExternalLink, MapPin, X, Check } from "lucide-react";
import { useCompare } from "@/lib/CompareContext";

export default function Compare() {
  const [params] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);
  const [data, setData] = useState(null);
  const { remove } = useCompare();

  useEffect(() => {
    if (!ids.length) return;
    api.comparePark(ids).then(setData);
  }, [params.get("ids")]); // eslint-disable-line

  if (!ids.length) return (
    <div className="App"><Nav /><div className="max-w-3xl mx-auto py-32 text-center">
      <h2 className="font-display text-2xl">Nothing to compare yet</h2>
      <p className="text-ink/60 mt-2">Add parks from the search results.</p>
      <Link to="/search" className="btn-primary mt-6 inline-flex">Browse parks</Link>
    </div></div>
  );

  if (!data) return (
    <div className="App"><Nav /><div className="max-w-6xl mx-auto py-12 px-4"><div className="h-8 w-40 skeleton-shimmer rounded mb-6" /><div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-96 rounded-xl skeleton-shimmer" />)}</div></div></div>
  );

  // Build union of all facility tokens across compared parks
  const allFacilities = Array.from(new Set(data.parks.flatMap((p) => p.facilities)));
  const cheapest = Math.min(...data.parks.map(p => p.pricing.per_night));
  const bestRated = Math.max(...data.parks.map(p => p.rating));
  const nearestBeach = Math.min(...data.parks.map(p => p.beach_distance_km));

  const Row = ({ label, children }) => (
    <div className="grid grid-cols-[140px_1fr] sm:grid-cols-[180px_1fr] border-t border-border">
      <div className="bg-sand p-3 text-xs uppercase tracking-wider font-bold text-ink/60">{label}</div>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${data.parks.length}, minmax(0, 1fr))` }}>{children}</div>
    </div>
  );

  return (
    <div className="App">
      <Nav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <span className="label-eyebrow">Side-by-side</span>
        <h1 className="font-display font-extrabold text-4xl mt-2" data-testid="compare-title">Compare {data.parks.length} parks</h1>

        <div className="mt-8 bg-white border border-border rounded-2xl overflow-hidden">
          <div className="grid grid-cols-[140px_1fr] sm:grid-cols-[180px_1fr]">
            <div className="bg-sand"></div>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${data.parks.length}, minmax(0, 1fr))` }} data-testid="compare-headers">
              {data.parks.map((p) => (
                <div key={p.slug} className="p-4 border-l border-border relative">
                  <button onClick={() => remove(p.slug)} className="absolute top-3 right-3 text-ink/40 hover:text-terracotta" data-testid={`compare-page-remove-${p.slug}`}>
                    <X className="w-4 h-4" />
                  </button>
                  <Link to={`/park/${p.slug}`}>
                    <div className="aspect-[16/10] rounded-lg overflow-hidden bg-sand mb-3">
                      <OperatorCover park={p} size="sm" />
                    </div>
                    <div className="label-eyebrow text-[10px]">{p.operator}</div>
                    <h3 className="font-display font-semibold text-lg leading-snug mt-1">{p.name}</h3>
                    <div className="text-xs text-ink/60 inline-flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{p.region}</div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <Row label="Price / night">
            {data.parks.map((p) => (
              <div key={p.slug} className={`p-4 border-l border-border ${p.pricing.per_night === cheapest ? "bg-forest/5" : ""}`}>
                <div className="font-display text-2xl font-bold text-forest">{formatGBP(p.pricing.per_night)}</div>
                <div className="text-xs text-ink/60">{formatGBP(p.pricing.total)} total</div>
                {p.pricing.per_night === cheapest && <span className="chip mt-2">Cheapest</span>}
              </div>
            ))}
          </Row>

          <Row label="Rating">
            {data.parks.map((p) => (
              <div key={p.slug} className={`p-4 border-l border-border ${p.rating === bestRated ? "bg-forest/5" : ""}`}>
                <div className="inline-flex items-center gap-1 font-semibold"><Star className="w-4 h-4 fill-terracotta text-terracotta" />{p.rating}</div>
                <div className="text-xs text-ink/60">{p.review_count.toLocaleString()} reviews</div>
                {p.rating === bestRated && <span className="chip mt-2">Best rated</span>}
              </div>
            ))}
          </Row>

          <Row label="Distance to beach">
            {data.parks.map((p) => (
              <div key={p.slug} className={`p-4 border-l border-border ${p.beach_distance_km === nearestBeach ? "bg-forest/5" : ""}`}>
                <div className="font-semibold">{p.beach_distance_km < 1 ? `${Math.round(p.beach_distance_km*1000)}m` : `${p.beach_distance_km}km`}</div>
                {p.beach_distance_km === nearestBeach && <span className="chip mt-2">Nearest</span>}
              </div>
            ))}
          </Row>

          <Row label="Pet friendly">
            {data.parks.map((p) => (
              <div key={p.slug} className="p-4 border-l border-border">{p.pet_friendly ? <Check className="w-4 h-4 text-forest" /> : <X className="w-4 h-4 text-ink/30" />}</div>
            ))}
          </Row>
          <Row label="Adults only">
            {data.parks.map((p) => (
              <div key={p.slug} className="p-4 border-l border-border">{p.adults_only ? <Check className="w-4 h-4 text-forest" /> : <X className="w-4 h-4 text-ink/30" />}</div>
            ))}
          </Row>

          {allFacilities.map((f) => (
            <Row key={f} label={FACILITY_LABELS[f] || f}>
              {data.parks.map((p) => (
                <div key={p.slug} className="p-4 border-l border-border">
                  {p.facilities.includes(f) ? <FacilityIcon name={f} /> : <X className="w-4 h-4 text-ink/30" />}
                </div>
              ))}
            </Row>
          ))}

          <Row label="">
            {data.parks.map((p) => (
              <div key={p.slug} className="p-4 border-l border-border">
                <a href={p.booking_url} target="_blank" rel="noopener noreferrer sponsored" className="btn-primary text-sm w-full" data-testid={`compare-view-deal-${p.slug}`}>
                  View deal <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ))}
          </Row>
        </div>
      </div>
      <Footer />
    </div>
  );
}
